import DrawnCirclesBackground from '@/components/atoms/DrawnCirclesBackground';
import { CIRCLE_SVG_SIZE_MOBILE, CIRCLES_SVG_DATA_MOBILE, OUTER_CIRCLE_SIZE_MOBILE } from '@/constants/dimensions';
import { Center, Flex, Image } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import CircleFillOutWithText from './CircleFillOutWithText';
import { THealthDomain, THealthDomainScores } from '@/types/Health.types';
import iconsPng from '@/assets/img/png/icons';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';

interface Props {
  totalScore: number;
  domainsData: THealthDomainScores;
  showPercentageText?: boolean;
  showPercentageTextOnTopOfCircle?: boolean;
  circleSvgId?: string;
}

const MARGIN_FROM_CENTER_CIRCLE_SIDE_TOP_ITEMS = '120px';
const MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS = '48px';
const MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS = '82px';

const MOBILE_SORTER_WELLNESS_DOMAINS_INDEX_ARRAY = [
  {
    index: 1,
    top: OUTER_CIRCLE_SIZE_MOBILE / 6.7,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_TOP_ITEMS,
  },
  {
    index: 0,
    top: OUTER_CIRCLE_SIZE_MOBILE / 6.7,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_TOP_ITEMS,
    right: undefined,
  },
  {
    index: 2,
    top: OUTER_CIRCLE_SIZE_MOBILE / 2.5,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
  },
  {
    index: 6,
    top: OUTER_CIRCLE_SIZE_MOBILE / 2.5,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
    right: undefined,
  },
  {
    index: 3,
    top: OUTER_CIRCLE_SIZE_MOBILE / 1.47,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS,
  },
  {
    index: 5,
    top: OUTER_CIRCLE_SIZE_MOBILE / 1.47,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS,
    right: undefined,
  },
  {
    index: 4,
    top: OUTER_CIRCLE_SIZE_MOBILE - 90,
    left: undefined,
    right: undefined,
  },
];

/**
 *
 * Must be put in a parent container with `maxWidth={'100%'} overflow={'hidden'}` to prevent horizontal scrolling on mobile.
 */
const WellnessStateCircleMobile = ({
  totalScore,
  domainsData,
  showPercentageText,
  showPercentageTextOnTopOfCircle,
  circleSvgId,
}: Props) => {
  const domainsSortedByScore = useMemo(() => {
    if (domainsData) {
      const wellnessDataArray = Object.entries(domainsData).map(([wellnessDomain, wellnessValue]) => ({
        domainName: wellnessDomain,
        value: wellnessValue,
      }));

      wellnessDataArray.sort((a, b) => (a.value > b.value ? 1 : -1));
      return wellnessDataArray;
    }

    return [];
  }, [domainsData]);

  return (
    <Center>
      <DrawnCirclesBackground
        circleSvgSize={CIRCLE_SVG_SIZE_MOBILE}
        circleSvgData={CIRCLES_SVG_DATA_MOBILE}
        circleSvgId={circleSvgId}
      >
        <Center
          width={126}
          height={126}
          borderRadius={'50%'}
          boxShadow={'0px 7.5px 90px 0px #4E1AEF36'}
          backgroundColor={'extra.white'}
        >
          <CircleFillOutWithText
            thickness={5}
            circleDiameter={80}
            percentageFilled={totalScore}
            percentageTextStyle={{ fontSize: 18, fontWeight: 700 }}
            svgId={`${circleSvgId}-total-score-example`}
          />
        </Center>
        {MOBILE_SORTER_WELLNESS_DOMAINS_INDEX_ARRAY.map((sortedInfo, i) => (
          <Flex
            key={i}
            position={'absolute'}
            top={`${sortedInfo.top}px`}
            left={sortedInfo.left}
            right={sortedInfo.right}
            transition={'top 1.5s'}
          >
            <CircleFillOutWithText
              thickness={3}
              circleDiameter={55}
              circleColor={getColorFromPercentage(domainsSortedByScore[sortedInfo.index].value)}
              percentageFilled={domainsSortedByScore[sortedInfo.index].value}
              showPercentageTextOnTopOfCircle={showPercentageTextOnTopOfCircle}
              showPercentageText={showPercentageText}
              svgId={`${circleSvgId}-domain-${i}`}
              centeredElement={
                <Image
                  opacity={1.5}
                  src={iconsPng.wellnessDomainsGray[domainsSortedByScore[sortedInfo.index].domainName as THealthDomain]}
                  height={'22px'}
                  width={'22px'}
                />
              }
            />
          </Flex>
        ))}
      </DrawnCirclesBackground>
    </Center>
  );
};

export default WellnessStateCircleMobile;
